import {
  type CanvasSettings,
  type Component,
  type ComponentInput,
  type Position,
  type Page,
  PageTemplate,
  ProjectRole,
} from "../../types";
import { generateId } from "../../utils";
import { responsivePositionTranslator } from "../../services/responsive/ResponsivePositionTranslator";

export const defaultCanvasSettings: CanvasSettings = {
  background: {
    type: "color",
    color: "#f9fafb",
  },
  grid: {
    enabled: true,
    size: 20,
    color: "#e5e7eb",
    opacity: 0.5,
    style: "solid",
    majorLines: {
      enabled: true,
      interval: 5,
      color: "#d1d5db",
      opacity: 0.7,
    },
    snapToGrid: true,
    snapThreshold: 10,
  },
  layout: {
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    maxWidth: null,
    maxHeight: null,
    centerContent: false,
    overflow: "visible",
  },
  theme: {
    name: "Default",
    colorScheme: "light",
    accentColor: "#3b82f6",
    borderRadius: 8,
    shadows: true,
    animations: true,
  },
  responsive: {
    breakpoints: {
      desktop: 1200,
      tablet: 768,
      mobile: 480,
    },
    scaling: {
      desktop: 1,
      tablet: 0.8,
      mobile: 0.6,
    },
    hideElementsOnMobile: false,
    touchOptimization: true,
  },
};

export function buildComponent(
  componentData: ComponentInput,
  currentPageId: string,
  currentProjectId?: string,
): Component {
  const now = new Date();

  return {
    ...componentData,
    id: componentData.id ?? generateId("component"),
    projectId: componentData.projectId ?? currentProjectId ?? "",
    pageId: currentPageId || componentData.pageId || "",
    properties: componentData.properties ?? {},
    size: componentData.size ?? { width: 300, height: 200 },
    style: componentData.style ?? {},
    createdAt: componentData.createdAt ?? now,
    updatedAt: componentData.updatedAt ?? now,
    children: componentData.children ?? [],
    order: componentData.order ?? 0,
    locked: componentData.locked ?? false,
    hidden: componentData.hidden ?? false,
    version: componentData.version ?? 1,
    createdBy: componentData.createdBy ?? "system",
    lastModifiedBy: componentData.lastModifiedBy ?? "system",
    lastModifiedAt: componentData.lastModifiedAt ?? now,
    isDeleted: componentData.isDeleted ?? false,
    parentId: componentData.parentId,
    responsivePosition:
      componentData.responsivePosition ??
      responsivePositionTranslator.generateDefaultResponsivePosition(
        componentData.type,
      ),
  } satisfies Component;
}

export function typedObjectKeys<T extends object>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

export function toIdEntries<T extends { id: string }>(
  items: readonly T[],
): [string, T][] {
  return items.map((item): [string, T] => [item.id, item]);
}

export function toIdMap<T extends { id: string }>(
  items: readonly T[],
): Map<string, T> {
  return new Map<string, T>(toIdEntries(items));
}

export interface ComponentClonePlan {
  components: Component[];
  idMap: Map<string, string>;
}

export interface ComponentClonePlanOptions {
  now?: Date;
  positionOffset?: Pick<Position, "x" | "y">;
  preserveExternalParentIds?: boolean;
  rootIds?: ReadonlySet<string>;
  rootNameSuffix?: string;
}

function mergeUniqueIds(
  primaryIds: readonly string[],
  secondaryIds: readonly string[],
): string[] {
  const seenIds = new Set<string>();
  const mergedIds: string[] = [];

  [...primaryIds, ...secondaryIds].forEach((id) => {
    if (seenIds.has(id)) {
      return;
    }

    seenIds.add(id);
    mergedIds.push(id);
  });

  return mergedIds;
}

function mergeUniqueComponentSnapshots(
  primaryComponents: readonly Component[],
  secondaryComponents: readonly Component[],
): Component[] {
  const seenIds = new Set<string>();
  const mergedComponents: Component[] = [];

  [...primaryComponents, ...secondaryComponents].forEach((component) => {
    if (seenIds.has(component.id)) {
      return;
    }

    seenIds.add(component.id);
    mergedComponents.push(component);
  });

  return mergedComponents;
}

function clonePositionWithOffset(
  position: Position | undefined,
  offset: Pick<Position, "x" | "y"> | undefined,
): Position | undefined {
  if (!position) {
    return undefined;
  }

  if (!offset) {
    return position;
  }

  return {
    ...position,
    x: position.x + offset.x,
    y: position.y + offset.y,
  };
}

function remapChildIds(
  children: readonly string[],
  idMap: ReadonlyMap<string, string>,
): string[] {
  return children.reduce<string[]>((remappedChildren, childId) => {
    const remappedChildId = idMap.get(childId);
    return remappedChildId
      ? [...remappedChildren, remappedChildId]
      : remappedChildren;
  }, []);
}

function remapParentId(
  parentId: string | undefined,
  idMap: ReadonlyMap<string, string>,
  preserveExternalParentIds: boolean,
): string | undefined {
  if (!parentId) {
    return undefined;
  }

  return idMap.get(parentId) ?? (preserveExternalParentIds ? parentId : undefined);
}

export function flattenComponentsForClone(
  components: readonly Component[],
): Component[] {
  const flatComponentById = toIdMap(components);
  const componentById = new Map<string, Component>();
  const orderedIds: string[] = [];
  const cacheChildIdsByParentId = new Map<string, string[]>();
  const parentIdByChildId = new Map<string, string>();
  const visitedIds = new Set<string>();

  const registerComponent = (component: Component): Component => {
    const authoritativeComponent = flatComponentById.get(component.id) ?? component;
    if (!componentById.has(component.id)) {
      componentById.set(component.id, authoritativeComponent);
      orderedIds.push(component.id);
    }

    return authoritativeComponent;
  };

  const registerParentChild = (parentId: string, childId: string): void => {
    const authoritativeChild = flatComponentById.get(childId);
    if (
      authoritativeChild?.parentId &&
      authoritativeChild.parentId !== parentId &&
      flatComponentById.has(authoritativeChild.parentId)
    ) {
      return;
    }

    const childIds = cacheChildIdsByParentId.get(parentId) ?? [];
    cacheChildIdsByParentId.set(parentId, mergeUniqueIds(childIds, [childId]));

    if (!parentIdByChildId.has(childId)) {
      parentIdByChildId.set(childId, parentId);
    }
  };

  const visitComponent = (component: Component): void => {
    const authoritativeComponent = registerComponent(component);
    if (visitedIds.has(authoritativeComponent.id)) {
      return;
    }

    visitedIds.add(authoritativeComponent.id);

    const childComponents = mergeUniqueComponentSnapshots(
      authoritativeComponent._childComponents ?? [],
      component._childComponents ?? [],
    );

    if (authoritativeComponent.parentId) {
      registerParentChild(authoritativeComponent.parentId, authoritativeComponent.id);
    }

    authoritativeComponent.children.forEach((childId) => {
      registerParentChild(authoritativeComponent.id, childId);
    });

    childComponents.forEach((childComponent) => {
      registerComponent(childComponent);
      registerParentChild(authoritativeComponent.id, childComponent.id);
      visitComponent(childComponent);
    });
  };

  components.forEach((component) => {
    visitComponent(component);
  });

  const knownIds = new Set(componentById.keys());

  return orderedIds.reduce<Component[]>((flattenedComponents, componentId) => {
    const component = componentById.get(componentId);
    if (!component) {
      return flattenedComponents;
    }

    const discoveredParentId = parentIdByChildId.get(component.id);
    const hasKnownParent =
      component.parentId !== undefined && knownIds.has(component.parentId);
    const children = mergeUniqueIds(
      component.children,
      cacheChildIdsByParentId.get(component.id) ?? [],
    ).filter((childId) => knownIds.has(childId));
    const normalizedComponent: Component = {
      ...component,
      parentId: hasKnownParent
        ? component.parentId
        : discoveredParentId ?? component.parentId,
      children,
      _childComponents: undefined,
    };

    return [...flattenedComponents, normalizedComponent];
  }, []);
}

export function collectComponentTreeForClone(
  rootId: string,
  components: readonly Component[],
): Component[] {
  const sourceComponents = flattenComponentsForClone(components);
  const componentById = toIdMap(sourceComponents);
  const childIdsByParentId = new Map<string, string[]>();

  sourceComponents.forEach((component) => {
    if (!component.parentId) {
      return;
    }

    const childIds = childIdsByParentId.get(component.parentId) ?? [];
    childIdsByParentId.set(component.parentId, [...childIds, component.id]);
  });

  const collectedIds = new Set<string>();
  const collect = (componentId: string): void => {
    if (collectedIds.has(componentId)) {
      return;
    }

    const component = componentById.get(componentId);
    if (!component) {
      return;
    }

    collectedIds.add(componentId);
    const childrenByParent = childIdsByParentId.get(componentId) ?? [];
    const childIds = [...component.children, ...childrenByParent];
    childIds.forEach(collect);
  };

  collect(rootId);

  return sourceComponents.filter((component) => collectedIds.has(component.id));
}

export function collectComponentTreesForClone(
  rootIds: readonly string[],
  components: readonly Component[],
): Component[] {
  const sourceComponents = flattenComponentsForClone(components);
  const selectedRootIds = getTopLevelCloneRootIds(rootIds, sourceComponents);
  const collectedIds = new Set<string>();

  return selectedRootIds.reduce<Component[]>((collectedComponents, rootId) => {
    const treeComponents = collectComponentTreeForClone(rootId, sourceComponents);
    const newTreeComponents = treeComponents.filter((component) => {
      if (collectedIds.has(component.id)) {
        return false;
      }

      collectedIds.add(component.id);
      return true;
    });

    return [...collectedComponents, ...newTreeComponents];
  }, []);
}

export function getTopLevelCloneRootIds(
  rootIds: readonly string[],
  components: readonly Component[],
): string[] {
  const sourceComponents = flattenComponentsForClone(components);
  const selectedIds = new Set(rootIds);
  const componentById = toIdMap(sourceComponents);

  return rootIds.filter((rootId) => {
    const component = componentById.get(rootId);
    if (!component) {
      return false;
    }

    let parentId = component.parentId;
    while (parentId) {
      if (selectedIds.has(parentId)) {
        return false;
      }
      parentId = componentById.get(parentId)?.parentId;
    }

    return true;
  });
}

export function cloneComponentSnapshot(component: Component): Component {
  return {
    ...structuredClone(component),
    _childComponents: undefined,
  };
}

export function createComponentClonePlan(
  components: readonly Component[],
  options: ComponentClonePlanOptions = {},
): ComponentClonePlan {
  const sourceComponents = flattenComponentsForClone(components);
  const now = options.now ?? new Date();
  const childIdsByParentId = new Map<string, string[]>();

  sourceComponents.forEach((component) => {
    if (!component.parentId) {
      return;
    }

    childIdsByParentId.set(component.parentId, [
      ...(childIdsByParentId.get(component.parentId) ?? []),
      component.id,
    ]);
  });

  const idMap = new Map<string, string>(
    sourceComponents.map((component): [string, string] => [
      component.id,
      generateId("component"),
    ]),
  );

  const clonedComponents = sourceComponents.map((component) => {
    const clonedComponent = cloneComponentSnapshot(component);
    const newId = idMap.get(component.id);

    if (!newId) {
      throw new Error(`Missing cloned component ID for ${component.id}`);
    }

    const shouldRename =
      options.rootNameSuffix &&
      (!options.rootIds || options.rootIds.has(component.id));

    return {
      ...clonedComponent,
      id: newId,
      name: shouldRename
        ? `${clonedComponent.name} ${options.rootNameSuffix}`
        : clonedComponent.name,
      position: clonePositionWithOffset(
        clonedComponent.position,
        options.positionOffset,
      ),
      parentId: remapParentId(
        clonedComponent.parentId,
        idMap,
        options.preserveExternalParentIds === true,
      ),
      children: remapChildIds(
        mergeUniqueIds(
          clonedComponent.children,
          childIdsByParentId.get(component.id) ?? [],
        ),
        idMap,
      ),
      _childComponents: undefined,
      createdAt: now,
      updatedAt: now,
    } satisfies Component;
  });

  return {
    components: clonedComponents,
    idMap,
  };
}

export function mergeIdMap<T extends { id: string }>(
  existing: ReadonlyMap<string, T>,
  additions: readonly T[],
): Map<string, T> {
  return new Map<string, T>([...existing, ...toIdEntries(additions)]);
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isPartialComponent(value: unknown): value is Partial<Component> {
  return isRecord(value);
}

export function getLegacyRootComponents(
  project: object,
): Partial<Component>[] | undefined {
  if (!("components" in project)) {
    return undefined;
  }

  const componentValue = Reflect.get(project, "components");
  if (!Array.isArray(componentValue)) {
    return undefined;
  }

  return componentValue.filter(isPartialComponent);
}

export function isResponsiveBreakpointKey(
  value: string,
): value is "mobile" | "tablet" | "desktop" {
  return value === "mobile" || value === "tablet" || value === "desktop";
}

export function ensureResponsivePosition(component: Component): Component {
  return component.responsivePosition
    ? component
    : {
        ...component,
        responsivePosition:
          responsivePositionTranslator.generateDefaultResponsivePosition(
            component.type,
          ),
      };
}

export function ensureResponsivePositions(
  components: readonly Component[],
): Component[] {
  return components.map(ensureResponsivePosition);
}

export function normalizePageCanvasSettings(
  canvasSettings?: Partial<CanvasSettings> | null,
): CanvasSettings {
  return {
    ...defaultCanvasSettings,
    ...canvasSettings,
    background: canvasSettings?.background ?? defaultCanvasSettings.background,
    grid: { ...defaultCanvasSettings.grid, ...canvasSettings?.grid },
    layout: { ...defaultCanvasSettings.layout, ...canvasSettings?.layout },
    theme: { ...defaultCanvasSettings.theme, ...canvasSettings?.theme },
    responsive: {
      ...defaultCanvasSettings.responsive,
      ...canvasSettings?.responsive,
    },
  };
}

export function createDefaultPage(
  projectId: string,
  ownerId: string,
  canvasSettings?: Partial<CanvasSettings> | null,
): Page {
  const now = new Date();

  return {
    id: generateId("page"),
    projectId,
    name: "Home",
    description: "Main overview and dashboard",
    order: 0,
    isDefault: true,
    template: PageTemplate.BLANK,
    navigation: {
      showInNavigation: true,
      allowNavigation: true,
      requiresAuth: false,
      icon: undefined,
      order: 0,
    },
    permissions: {
      canView: [
        ProjectRole.OWNER,
        ProjectRole.ADMIN,
        ProjectRole.EDITOR,
        ProjectRole.VIEWER,
      ],
      canEdit: [ProjectRole.OWNER, ProjectRole.ADMIN, ProjectRole.EDITOR],
      canDelete: [ProjectRole.OWNER, ProjectRole.ADMIN],
      canShare: [ProjectRole.OWNER, ProjectRole.ADMIN],
      inheritFromProject: true,
      customPermissions: [],
    },
    canvasSettings: normalizePageCanvasSettings(canvasSettings),
    metadata: {
      viewCount: 0,
      editCount: 0,
      componentCount: 0,
      tags: [],
      customFields: {},
    },
    createdBy: ownerId,
    lastModifiedBy: ownerId,
    lastModifiedAt: now,
    createdAt: now,
    updatedAt: now,
  };
}

export function hydrateProjectComponent(
  component: Partial<Component>,
  projectId: string,
  pageId: string,
  ownerId: string,
): Component | null {
  if (!component.type) {
    console.warn(
      `[hydration] Skipping legacy component with missing type (id: ${component.id ?? "unknown"})`,
    );
    return null;
  }

  return {
    id: component.id ?? generateId("component"),
    projectId,
    pageId,
    name: component.name ?? `${component.type} Component`,
    type: component.type,
    properties: component.properties ?? {},
    position: component.position ?? { x: 0, y: 0, z: 0 },
    size: component.size ?? { width: 200, height: 100 },
    style: component.style ?? {},
    responsivePosition:
      component.responsivePosition ??
      responsivePositionTranslator.generateDefaultResponsivePosition(
        component.type,
      ),
    parentId: component.parentId,
    children: component.children ?? [],
    order: component.order ?? 0,
    locked: component.locked ?? false,
    hidden: component.hidden ?? false,
    version: component.version ?? 1,
    createdBy: component.createdBy ?? ownerId,
    lastModifiedBy: component.lastModifiedBy ?? ownerId,
    lastModifiedAt: component.lastModifiedAt
      ? new Date(component.lastModifiedAt)
      : new Date(),
    isDeleted: component.isDeleted ?? false,
    deletedAt: component.deletedAt
      ? new Date(component.deletedAt)
      : undefined,
    createdAt: component.createdAt ? new Date(component.createdAt) : new Date(),
    updatedAt: component.updatedAt ? new Date(component.updatedAt) : new Date(),
    isSubComponent: component.isSubComponent,
    subComponentType: component.subComponentType,
    interactions: component.interactions,
    metadata: component.metadata,
    zIndex: component.zIndex,
  };
}
